import { ArrowLeft } from 'lucide-preact'
import { useEffect, useState } from 'preact/hooks'
import { text } from '#contract'
import { useScreenStack } from '../../app/screen-stack.ts'
import { Button } from '../../components/button.tsx'
import { ScreenPanel } from '../../components/screen-panel.tsx'
import { useT } from '../../i18n/index.ts'
import type { Credits, CreditsEntry } from './credits-data.ts'
import { loadCredits } from './credits-data.ts'
import styles from './credits-screen.module.css'

type State =
  { readonly status: 'loading' | 'error' } | { readonly status: 'ready'; readonly credits: Credits }

/** Every asset and package the game ships, with its license. Opened over a menu. */
export function CreditsScreen() {
  const t = useT()
  const { back } = useScreenStack()
  const [state, setState] = useState<State>({ status: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    loadCredits(controller.signal)
      .then((credits) => {
        setState({ status: 'ready', credits })
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ status: 'error' })
      })
    return () => {
      controller.abort()
    }
  }, [])

  return (
    <ScreenPanel
      title={t(text('credits.title'))}
      footer={
        <Button variant="ghost" icon={ArrowLeft} autoFocus sound="back" onClick={back}>
          {t(text('credits.back'))}
        </Button>
      }
    >
      {state.status === 'loading' && <p class={styles.status}>{t(text('credits.loading'))}</p>}
      {state.status === 'error' && <p class={styles.status}>{t(text('credits.error'))}</p>}
      {state.status === 'ready' &&
        state.credits.groups.map((group) => (
          <section key={group.id} class={styles.group}>
            <h2 class={styles.heading}>{t(text(`credits.group.${group.id}`))}</h2>
            <ul class={styles.entries}>
              {group.entries.map((entry) => (
                <Entry key={`${entry.title}|${entry.source.url}`} entry={entry} />
              ))}
            </ul>
          </section>
        ))}
    </ScreenPanel>
  )
}

function Entry({ entry }: { entry: CreditsEntry }) {
  const t = useT()
  return (
    <li class={styles.entry}>
      <span class={styles.title}>{entry.title}</span>
      {entry.authors.length > 0 && (
        <span>{t(text('credits.by', { authors: entry.authors.join(', ') }))}</span>
      )}
      <span class={styles.links}>
        <a href={entry.license.url} target="_blank" rel="noopener noreferrer">
          {entry.license.name}
        </a>
        <a href={entry.source.url} target="_blank" rel="noopener noreferrer">
          {entry.source.name}
        </a>
      </span>
      {entry.creditLine !== undefined && <span class={styles.note}>{entry.creditLine}</span>}
      {entry.changes !== undefined && <span class={styles.note}>{entry.changes}</span>}
    </li>
  )
}
